import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Form } from 'reactstrap';
import { AudioDropZone } from '../../DropZones/AudioDropZone';
import { useTranscribeApi } from './TranscribeApi';
import { SubmitButton } from '../shared/SubmitButton';
import { AI_UTILITIES } from '../../../constants/aiUtilities';
import { useAuth } from '../../../components/Auth/AuthContext';
import { useNotification } from '../../../components/index';
import { useMountedRef } from '../../../components/hooks/useMountedRef';
import { useRepeatProcessingLoader } from '../shared/hooks/useRepeatProcessingLoader';
import { CommentInput } from '../shared/CommentInput';
import { StartTimePicker } from '../shared/StartTimePicker';
import { NextUtilitySelector } from '../shared/NextUtilitySelector';
import { useUserPreferences } from '../shared/hooks/useUserPreferences';
import { DEFAULT_OUTPUT_FORMAT, OUTPUT_FORMATS } from '../shared/constants/outputFormats';

export const TranscribeLoader = () => {
    const history = useHistory();
    const location = useLocation();
    const { checkPermission } = useAuth();
    const { getPreferences, savePreferences } = useUserPreferences();

    const [files, setFiles] = useState([]);
    const [comment, setComment] = useState('');
    const [startTime, setStartTime] = useState(null);
    const [selectedUtility, setSelectedUtility] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const [defaultOutputFormat, setDefaultOutputFormat] = useState(DEFAULT_OUTPUT_FORMAT);
    const [defaultTemplate, setDefaultTemplate] = useState(null);

    // Режим повторной обработки
    const isRepeatMode = useRef(location.state?.isRepeatProcessing || false);
    const userInteracted = useRef(false);

    const { submitTranscribeRequest } = useTranscribeApi();
    const {
        showError,
        showWarning
    } = useNotification();
    const isMounted = useMountedRef();

    const getAvailableUtilities = () => {
        const options = [{ value: null, label: 'Не выбрано' }];
        if (checkPermission(AI_UTILITIES.SUMMARIZE_RESUME.permission)) {
            options.push({
                value: AI_UTILITIES.SUMMARIZE_RESUME.id,
                label: AI_UTILITIES.SUMMARIZE_RESUME.name,
            });
        }
        if (checkPermission(AI_UTILITIES.RETELLING.permission)) {
            options.push({
                value: AI_UTILITIES.RETELLING.id,
                label: AI_UTILITIES.RETELLING.name,
            });
        }
        return options;
    };

    const availableUtilities = getAvailableUtilities();

    useEffect(() => {
        if (isRepeatMode.current) {
            return;
        }
        const loadPreferences = async () => {
            try {
                const prefs = await getPreferences(AI_UTILITIES.TRANSCRIBE.id);
                // Валидация формата
                const formats = availableUtilities.map(opt => opt.value);
                if (prefs.selectedUtility && formats.includes(prefs.selectedUtility)) {
                    setSelectedUtility(prefs.selectedUtility);
                } else {
                    setSelectedUtility(null);
                }
                // Валидация выходного формата
                const validFormats = OUTPUT_FORMATS.map(f => f.value);
                if (prefs.defaultOutputFormat && validFormats.includes(prefs.defaultOutputFormat)) {
                    setDefaultOutputFormat(prefs.defaultOutputFormat);
                } else {
                    setDefaultOutputFormat(DEFAULT_OUTPUT_FORMAT);
                }
                // Шаблон пока не проверяем (загрузится позже в DropZoneBase)
                if (prefs.defaultTemplate) {
                    setDefaultTemplate(prefs.defaultTemplate);
                } else {
                    setDefaultTemplate(null);
                }
            } catch (error) {
                // Если предпочтений нет или ошибка – используем дефолты
                console.warn('Failed to load preferences', error);
            }
        };
        loadPreferences();
    }, []);

    useRepeatProcessingLoader({
        locationState: location.state,
        setFiles,
        setIsLoading,
        setIsInitializing,
        showError,
        isMounted,
        onLoaded: (state) => {
            setComment(state.comment || '');
        },
    });

    const saveCurrentPreferences = async () => {
        if (isRepeatMode.current && !userInteracted.current) return;
        try {
            await savePreferences(AI_UTILITIES.TRANSCRIBE.id, {
                selectedUtility,
                defaultOutputFormat,
                defaultTemplate,
            });
        } catch (error) {
            console.error('Failed to save preferences', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (files.length === 0) {
            showWarning("Пожалуйста, выберите файлы для обработки");
            return;
        }

        try {
            saveCurrentPreferences();
        }
        catch (error) {
            console.error(error);
        }

        setIsLoading(true);
        try {
            const startTimeString = startTime ? startTime.toISOString() : '';
            const { id, identifier } = await submitTranscribeRequest(files, comment, selectedUtility, startTimeString);
            if (isMounted.current) {
                history.push({
                    pathname: `${AI_UTILITIES.TRANSCRIBE.path}/${identifier}`,
                    state: {
                        id,
                        fileNames: files.map(f => f.file.name),
                        comment: comment
                    }
                });
            }
        } catch (error) {
            showError(error.message);
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    };

    const handleFilesChange = (newFiles) => {
        if (!isLoading && !isInitializing) {
            setFiles(newFiles);
        }
    };

    const handleUtilityChange = (e) => {
        const value = e.target.value;
        setSelectedUtility(value === 'null' ? null : value);
        // Отмечаем взаимодействие и сохраняем
        userInteracted.current = true;
        isRepeatMode.current = false; // выходим из режима повторной загрузки
        
    };

    // Изменение формата (вызывается из DropZone)
    const handleFormatChange = (newFormat) => {
        setDefaultOutputFormat(newFormat);
        userInteracted.current = true;
        isRepeatMode.current = false;
    };

    // Изменение шаблона
    const handleTemplateChange = (newTemplate) => {
        setDefaultTemplate(newTemplate);
        userInteracted.current = true;
        isRepeatMode.current = false;
    };

    return (
        <Form onSubmit={handleSubmit} className="speakers-form-container">

            <NextUtilitySelector
                availableUtilities={availableUtilities}
                selectedUtility={selectedUtility}
                onChange={handleUtilityChange}
                disabled={isLoading || isInitializing}
            />

            <CommentInput
                id="transcribeComment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={isLoading || isInitializing}
            />

            <StartTimePicker
                value={startTime}
                onChange={setStartTime}
                disabled={isLoading || isInitializing}
            />

            <AudioDropZone
                files={files}
                isLoading={isLoading || isInitializing}
                onFilesChange={handleFilesChange}
                showFormatSelector={true}
                maxFiles={2}
                showTemplateSelector={true}
                defaultOutputFormat={defaultOutputFormat} 
                defaultTemplate={defaultTemplate}
                onFormatChange={handleFormatChange} 
                onTemplateChange={handleTemplateChange}
            />

            <SubmitButton
                isLoading={isLoading}
                text="Отправить на обработку ИИ"
                disabled={isLoading || isInitializing || files.length === 0}
            />
        </Form>
    );
};